import {
  AnimalType,
  MedType,
  IndicationType,
  RegimenType,
  DosageMap,
  MedicationMap
} from '../types/dosage-types';

// Medication information database
export const medicationData: MedicationMap = {
  [MedType.TOLTRAZURIL]: {
    name: 'Toltrazuril',
    concentrationMgPerMl: 50, // Common 5% solution
    commonFormulations: ['5% oral solution (50mg/ml)', '2.5% solution (25mg/ml)'],
    warnings: ['Not for use in animals intended for human consumption during treatment'],
    activeIngredient: 'Toltrazuril'
  },

  [MedType.SULFADIMETHOXINE]: {
    name: 'Sulfadimethoxine (Albon)',
    concentrationMgPerMl: 50, // 5% solution
    tabletStrengthMg: 125,
    commonFormulations: ['5% oral solution (50mg/ml)', '125mg tablets', '250mg tablets'],
    warnings: ['Maintain adequate water intake', 'Monitor for crystalluria'],
    activeIngredient: 'Sulfadimethoxine'
  },

  [MedType.FENBENDAZOLE]: {
    name: 'Fenbendazole',
    concentrationMgPerMl: 100, // 10% suspension
    tabletStrengthMg: 222,
    commonFormulations: ['10% oral suspension (100mg/ml)', '222mg tablets', 'Granules'],
    warnings: ['Safe in pregnant animals', 'May cause mild GI upset'],
    activeIngredient: 'Fenbendazole'
  },

  [MedType.OMEPRAZOLE]: {
    name: 'Omeprazole',
    tabletStrengthMg: 20,
    commonFormulations: ['20mg delayed-release tablets', '40mg tablets', 'Oral paste'],
    warnings: ['Give on empty stomach', 'May interact with other medications'],
    activeIngredient: 'Omeprazole'
  },

  [MedType.IVERMECTIN]: {
    name: 'Ivermectin',
    concentrationMgPerMl: 10, // 1% injection solution
    commonFormulations: ['1% injection (10mg/ml)', '0.08% oral solution', 'Pour-on'],
    warnings: ['Toxic to some dog breeds (MDR1 mutation)', 'Not for use in dairy animals'],
    activeIngredient: 'Ivermectin'
  },

  [MedType.ALBENDAZOLE]: {
    name: 'Albendazole',
    concentrationMgPerMl: 100, // 10% suspension
    tabletStrengthMg: 300,
    commonFormulations: ['10% oral suspension (100mg/ml)', '300mg tablets'],
    warnings: ['Teratogenic - not for pregnant animals', 'Monitor liver function'],
    activeIngredient: 'Albendazole'
  },

  [MedType.METRONIDAZOLE]: {
    name: 'Metronidazole',
    concentrationMgPerMl: 50, // 5% solution
    tabletStrengthMg: 250,
    commonFormulations: ['5% oral solution (50mg/ml)', '250mg tablets', '500mg tablets'],
    warnings: ['May cause neurological signs at high doses', 'Bitter taste'],
    activeIngredient: 'Metronidazole'
  },

  [MedType.AMPROLIUM]: {
    name: 'Amprolium',
    concentrationMgPerMl: 96, // 9.6% solution
    commonFormulations: ['9.6% oral solution (96mg/ml)', '20% powder'],
    warnings: ['May cause thiamine deficiency with prolonged use', 'Maintain adequate nutrition'],
    activeIngredient: 'Amprolium'
  }
};

// Comprehensive dosage regimen database
export const dosageData: DosageMap = {
  [AnimalType.DOG]: {
    [MedType.TOLTRAZURIL]: {
      [IndicationType.TREATMENT]: {
        mgPerKgRegularDose: 20,
        doseFrequencyHours: 24,
        regimenType: RegimenType.SINGLE,
        regimenFrequency: 1,
        minWeightKg: 0.5,
        notes: 'Single dose treatment for coccidiosis'
      }
    },
    [MedType.SULFADIMETHOXINE]: {
      [IndicationType.TREATMENT]: {
        mgPerKgLoadingDose: 55,
        mgPerKgRegularDose: 27.5,
        doseFrequencyHours: 24,
        regimenType: RegimenType.MULTI,
        regimenFrequency: 7,
        withdrawalPeriodDays: 5,
        notes: 'Loading dose on day 1, then regular dose for 6 days'
      }
    },
    [MedType.FENBENDAZOLE]: {
      [IndicationType.TREATMENT]: {
        mgPerKgRegularDose: 50,
        doseFrequencyHours: 24,
        regimenType: RegimenType.MULTI,
        regimenFrequency: 3,
        notes: 'Give for 3 consecutive days'
      }
    },
    [MedType.OMEPRAZOLE]: {
      [IndicationType.TREATMENT]: {
        mgPerKgRegularDose: 1,
        doseFrequencyHours: 24,
        regimenType: RegimenType.MULTI,
        regimenFrequency: 14,
        notes: 'Give on empty stomach, 1 hour before feeding'
      }
    }
  },

  [AnimalType.CAT]: {
    [MedType.TOLTRAZURIL]: {
      [IndicationType.TREATMENT]: {
        mgPerKgRegularDose: 20,
        doseFrequencyHours: 24,
        regimenType: RegimenType.SINGLE,
        regimenFrequency: 1,
        minWeightKg: 0.3,
        notes: 'Single dose treatment for coccidiosis'
      }
    },
    [MedType.SULFADIMETHOXINE]: {
      [IndicationType.TREATMENT]: {
        mgPerKgLoadingDose: 50,
        mgPerKgRegularDose: 25,
        doseFrequencyHours: 24,
        regimenType: RegimenType.MULTI,
        regimenFrequency: 7,
        withdrawalPeriodDays: 5,
        notes: 'Loading dose on day 1, then regular dose for 6 days'
      }
    },
    [MedType.FENBENDAZOLE]: {
      [IndicationType.TREATMENT]: {
        mgPerKgRegularDose: 50,
        doseFrequencyHours: 24,
        regimenType: RegimenType.MULTI,
        regimenFrequency: 5,
        notes: 'Give for 5 consecutive days for cats'
      }
    },
    [MedType.METRONIDAZOLE]: {
      [IndicationType.TREATMENT]: {
        mgPerKgRegularDose: 10,
        doseFrequencyHours: 12,
        regimenType: RegimenType.MULTI,
        regimenFrequency: 10,
        notes: 'Give twice daily for 5 days'
      }
    }
  },

  [AnimalType.CHICKEN]: {
    [MedType.TOLTRAZURIL]: {
      [IndicationType.TREATMENT]: {
        mgPerKgRegularDose: 25,
        doseFrequencyHours: 24,
        regimenType: RegimenType.MULTI,
        regimenFrequency: 2,
        withdrawalPeriodDays: 0,
        notes: 'Give for 2 consecutive days in drinking water'
      },
      [IndicationType.PREVENTION]: {
        mgPerKgRegularDose: 25,
        doseFrequencyHours: 24,
        regimenType: RegimenType.SINGLE,
        regimenFrequency: 1,
        withdrawalPeriodDays: 0,
        notes: 'Single preventive dose'
      }
    },
    [MedType.SULFADIMETHOXINE]: {
      [IndicationType.TREATMENT]: {
        mgPerKgLoadingDose: 55,
        mgPerKgRegularDose: 110,
        doseFrequencyHours: 24,
        regimenType: RegimenType.MULTI,
        regimenFrequency: 5,
        withdrawalPeriodDays: 5,
        notes: 'In drinking water, loading dose day 1, regular dose days 2-5'
      }
    },
    [MedType.AMPROLIUM]: {
      [IndicationType.TREATMENT]: {
        mgPerKgRegularDose: 10,
        doseFrequencyHours: 24,
        regimenType: RegimenType.MULTI,
        regimenFrequency: 5,
        withdrawalPeriodDays: 0,
        notes: 'In drinking water for 5 days'
      },
      [IndicationType.PREVENTION]: {
        mgPerKgRegularDose: 5,
        doseFrequencyHours: 24,
        regimenType: RegimenType.MULTI,
        regimenFrequency: 7,
        withdrawalPeriodDays: 0,
        notes: 'In drinking water for 7 days'
      }
    }
  },

  [AnimalType.GOAT]: {
    [MedType.TOLTRAZURIL]: {
      [IndicationType.TREATMENT]: {
        mgPerKgRegularDose: 20,
        doseFrequencyHours: 24,
        regimenType: RegimenType.SINGLE,
        regimenFrequency: 1,
        withdrawalPeriodDays: 28,
        minWeightKg: 5,
        notes: 'Single oral dose, observe withdrawal period'
      }
    },
    [MedType.SULFADIMETHOXINE]: {
      [IndicationType.TREATMENT]: {
        mgPerKgLoadingDose: 55,
        mgPerKgRegularDose: 27.5,
        doseFrequencyHours: 24,
        regimenType: RegimenType.MULTI,
        regimenFrequency: 5,
        withdrawalPeriodDays: 5,
        notes: 'Loading dose day 1, then regular dose for 4 days'
      }
    },
    [MedType.FENBENDAZOLE]: {
      [IndicationType.TREATMENT]: {
        mgPerKgRegularDose: 10,
        doseFrequencyHours: 24,
        regimenType: RegimenType.MULTI,
        regimenFrequency: 3,
        withdrawalPeriodDays: 8,
        notes: 'Give for 3 consecutive days'
      }
    }
  },

  [AnimalType.HORSE]: {
    [MedType.FENBENDAZOLE]: {
      [IndicationType.TREATMENT]: {
        mgPerKgRegularDose: 7.5,
        doseFrequencyHours: 24,
        regimenType: RegimenType.SINGLE,
        regimenFrequency: 1,
        minWeightKg: 100,
        maxWeightKg: 800,
        notes: 'Single dose, paste formulation preferred'
      }
    },
    [MedType.IVERMECTIN]: {
      [IndicationType.TREATMENT]: {
        mgPerKgRegularDose: 0.2,
        doseFrequencyHours: 24,
        regimenType: RegimenType.SINGLE,
        regimenFrequency: 1,
        minWeightKg: 100,
        maxWeightKg: 800,
        notes: 'Single dose, oral paste preferred'
      }
    },
    [MedType.OMEPRAZOLE]: {
      [IndicationType.TREATMENT]: {
        mgPerKgRegularDose: 2,
        doseFrequencyHours: 24,
        regimenType: RegimenType.MULTI,
        regimenFrequency: 28,
        notes: 'Daily for gastric ulcer treatment, give on empty stomach'
      }
    }
  },

  [AnimalType.RABBIT]: {
    [MedType.TOLTRAZURIL]: {
      [IndicationType.TREATMENT]: {
        mgPerKgRegularDose: 25,
        doseFrequencyHours: 24,
        regimenType: RegimenType.MULTI,
        regimenFrequency: 2,
        minWeightKg: 0.5,
        maxWeightKg: 8,
        notes: 'Give for 2 consecutive days'
      }
    },
    [MedType.SULFADIMETHOXINE]: {
      [IndicationType.TREATMENT]: {
        mgPerKgLoadingDose: 50,
        mgPerKgRegularDose: 25,
        doseFrequencyHours: 24,
        regimenType: RegimenType.MULTI,
        regimenFrequency: 7,
        notes: 'Loading dose day 1, then regular dose for 6 days'
      }
    },
    [MedType.FENBENDAZOLE]: {
      [IndicationType.TREATMENT]: {
        mgPerKgRegularDose: 20,
        doseFrequencyHours: 24,
        regimenType: RegimenType.MULTI,
        regimenFrequency: 5,
        notes: 'Give for 5 consecutive days'
      }
    }
  }
};

// Helper function to get dosage regimen
export function getDosageRegimen(
  animalType: AnimalType,
  medType: MedType,
  indicationType: IndicationType
) {
  return dosageData[animalType]?.[medType]?.[indicationType];
}

// Helper function to get medication info
export function getMedicationInfo(medType: MedType) {
  return medicationData[medType];
}